// Direct HubSpot API submission utility
export interface FormSubmissionData {
  // Required fields
  firstname: string;
  lastname: string;
  email: string;

  // Optional contact fields
  company?: string;
  phone?: string;
  message?: string;

  // Product interest tracking (hidden from user)
  product_interest?: string;
  form_source?: string;

  // Show Leads custom properties
  role?: string;
  crops?: string;
  current_pain_point?: string;
  timeline?: string;

  // Context (not sent as HubSpot fields; used in `context` block)
  page_url?: string;
  page_title?: string;
  user_agent?: string;
}

// HubSpot property names that submitViaHubSpotForm will forward as form fields.
// Anything else in the payload is a bug (and will be warned about).
const HUBSPOT_FIELD_ALLOWLIST = new Set<string>([
  'firstname',
  'lastname',
  'email',
  'company',
  'phone',
  'message',
  'product_interest',
  'form_source',
  'role',
  'crops',
  'current_pain_point',
  'timeline',
]);

// Keys that are intentionally not forwarded as HubSpot fields.
const HUBSPOT_CONTEXT_KEYS = new Set<string>([
  'page_url',
  'page_title',
  'user_agent',
]);

// Submit contact data to HubSpot using the most reliable method
export const submitToHubSpot = async (formData: FormSubmissionData, specificFormId?: string): Promise<boolean> => {
  // For newsletter subscriptions, use a different form ID
  const isNewsletter = formData.form_source === 'Newsletter Popup';
  const formId = specificFormId || (isNewsletter ? '26f07e8b-36f1-4819-b60e-cc284ab5e8de' : '1cee315a-8f31-4333-b533-4b3b6ac556e0');
  
  console.log(`Using form ID: ${formId} for ${isNewsletter ? 'newsletter' : 'contact form'}`);

  try {
    console.log('🔄 Submitting contact to HubSpot:', {
      email: formData.email,
      form_source: formData.form_source
    });

    // Try HubSpot form submission first (most reliable with your form ID)
    const formSuccess = await submitViaHubSpotForm(formData, formId);
    if (formSuccess) {
      return true;
    }

    // Fallback to tracking method
    const trackingSuccess = await submitViaHubSpotTracking(formData);
    if (trackingSuccess) {
      return true;
    }

    // Side-effect logging only; does not count as a successful submission.
    logForManualProcessing(formData);
    return false;

  } catch (error) {
    console.error('❌ Error submitting to HubSpot:', error);

    // Side-effect logging only; the submission still failed.
    logForManualProcessing(formData);
    return false;
  }
};

// Primary method: Use your specific HubSpot form
const submitViaHubSpotForm = async (formData: FormSubmissionData, formId: string): Promise<boolean> => {
  try {
    const portalId = '243150835';
    console.log(`Submitting to HubSpot form ${formId} in portal ${portalId}`);
    console.log('📝 Form data:', {
      email: formData.email,
      firstname: formData.firstname,
      form_source: formData.form_source,
      form_id: formId
    });
    
    // Build fields from the allowlist. Warn on unknown keys so future drift surfaces.
    const fields: Array<{ name: string; value: string }> = [];
    for (const [key, value] of Object.entries(formData)) {
      if (HUBSPOT_CONTEXT_KEYS.has(key)) continue;
      if (!HUBSPOT_FIELD_ALLOWLIST.has(key)) {
        console.warn(
          `[hubspotForms] Dropping unknown HubSpot field "${key}". ` +
          `Add it to HUBSPOT_FIELD_ALLOWLIST if it is a valid HubSpot property.`
        );
        continue;
      }
      if (value === undefined || value === null || value === '') continue;
      fields.push({ name: key, value: String(value) });
    }

    // Prepare form data for HubSpot
    const submissionData = {
      fields,
      context: {
        pageUri: formData.page_url || window.location.href,
        pageName: formData.page_title || document.title,
        hutk: getCookie('hubspotutk'),
      },
      legalConsentOptions: {
        consent: {
          consentToProcess: true,
          text: "I agree to allow CTC Waterworks to store and process my personal data.",
          communications: [
            {
              value: true,
              subscriptionTypeId: 999,
              text: "I agree to receive marketing communications from CTC Waterworks."
            }
          ]
        }
      }
    };

    // Submit to your specific form
    const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Referrer-Policy': 'strict-origin-when-cross-origin',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(submissionData)
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Form submitted successfully to HubSpot form:', formId);
      return true;
    } else {
      const errorText = await response.text();
      console.log('⚠️ HubSpot form submission failed:', response.status, errorText);
      return false;
    }
  } catch (error) {
    console.error('❌ HubSpot form submission error:', error);
    return false;
  }
};

// Fallback method: Use HubSpot tracking and analytics
const submitViaHubSpotTracking = async (formData: FormSubmissionData): Promise<boolean> => {
  try {
    console.log('📊 Using HubSpot tracking method...');
    
    // Use HubSpot's analytics tracking if available
    if (window.hbspt && window.hbspt.analytics) {
      // Track a custom event with the form data
      window.hbspt.analytics.trackEvent('contact_form_submission', {
        email: formData.email,
        firstname: formData.firstname,
        lastname: formData.lastname,
        company: formData.company,
        phone: formData.phone,
        message: formData.message,
        product_interest: formData.product_interest,
        form_source: formData.form_source,
        page_url: window.location.href,
        submission_method: 'hubspot_tracking'
      });
      
      console.log('✅ Contact data tracked via HubSpot analytics');
      
      // Also try to identify the visitor
      if (window.hbspt.analytics.identify) {
        window.hbspt.analytics.identify({
          email: formData.email,
          firstname: formData.firstname,
          lastname: formData.lastname,
          company: formData.company
        });
        console.log('✅ Visitor identified in HubSpot');
      }
      
      return true;
    } else {
      console.log('⏳ HubSpot tracking not available');
      return false;
    }
  } catch (error) {
    console.error('❌ HubSpot tracking method failed:', error);
    return false;
  }
};

// Side-effect logging for failed submissions. Does NOT indicate success —
// callers must treat the surrounding submitToHubSpot result as a failure.
const logForManualProcessing = (formData: FormSubmissionData): void => {
  console.warn('📧 FALLBACK: HubSpot submission failed; logging contact data for manual processing:', {
    ...formData,
    timestamp: new Date().toISOString(),
    submission_method: 'fallback_logging'
  });
};

// Helper function to get cookie value
function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
}

// Parse full name into first and last name
export const parseFullName = (fullName: string): { firstname: string; lastname: string } => {
  const nameParts = fullName.trim().split(' ');
  
  if (nameParts.length === 1) {
    return {
      firstname: nameParts[0],
      lastname: ''
    };
  } else if (nameParts.length === 2) {
    return {
      firstname: nameParts[0],
      lastname: nameParts[1]
    };
  } else {
    // More than 2 parts - first name is first part, last name is everything else
    return {
      firstname: nameParts[0],
      lastname: nameParts.slice(1).join(' ')
    };
  }
};

// Product interest mapping - CLEAN DATA FOR HUBSPOT
export const PRODUCT_INTERESTS = {
  'general': 'General Inquiry',
  'sulfur-burners': 'Sulfur Burners',
  'hydrocoolers': 'Hydrocoolers', 
  'dpa-drenchers': 'DPA Drenchers',
  'filtration': 'Filtration'
} as const;

// Form source mapping - CLEAN DATA FOR HUBSPOT
export const FORM_SOURCES = {
  'newsletter_popup': 'Newsletter Popup',
  'hero_cta': 'Hero Section CTA',
  'navbar_contact': 'Navbar Contact Button',
  'product_quote': 'Product Quote Button',
  'footer_contact': 'Footer Contact',
  'guide_download': 'Guide Download',
  'water_evaluation': 'Water Evaluation Request',
  'seasonal_banner_specialist_cta': 'Seasonal Banner - Specialist CTA',
  'flood_resilience_page': 'Flood Resilience Page',
  'flood_irrigation_filtration_page': 'Flood Irrigation Filtration Page',
  'municipal_flood_intakes_page': 'Municipal Flood-Resilient Intakes Page'
} as const;

// Enhanced HubSpot tracking initialization
export const initializeHubSpotTracking = () => {
  console.log('Initializing HubSpot tracking...');
  
  // Ensure HubSpot tracking is properly initialized
  if (window.hbspt && window.hbspt.analytics) {
    console.log('✅ HubSpot tracking initialized');
    
    // Set up enhanced tracking
    window.hbspt.analytics.trackPageView();
    
    // Track visitor information if available
    const visitorInfo = {
      page_url: window.location.href,
      page_title: document.title,
      referrer: document.referrer,
      user_agent: navigator.userAgent,
      timestamp: new Date().toISOString()
    };
    
    window.hbspt.analytics.trackEvent('page_visit', visitorInfo);
  } else {
    console.log('⏳ HubSpot tracking not yet available, will retry...');
    
    // Retry after a short delay
    setTimeout(() => {
      if (window.hbspt && window.hbspt.analytics) {
        console.log('✅ HubSpot tracking initialized (delayed)');
        window.hbspt.analytics.trackPageView();
      }
    }, 2000);
  }
};

// Generic HubSpot form submission with custom fields
export interface HubSpotFormSubmission {
  formId: string;
  fields: Record<string, string>;
  context?: {
    pageUri?: string;
    pageName?: string;
  };
}

export const submitHubSpotForm = async (submission: HubSpotFormSubmission): Promise<boolean> => {
  try {
    const portalId = '243150835';
    console.log(`Submitting to HubSpot form ${submission.formId} in portal ${portalId}`);

    const submissionData = {
      fields: Object.entries(submission.fields).map(([name, value]) => ({
        name,
        value: value || '',
      })),
      context: {
        pageUri: submission.context?.pageUri || window.location.href,
        pageName: submission.context?.pageName || document.title,
        hutk: getCookie('hubspotutk'),
      },
    };

    const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${submission.formId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(submissionData),
    });

    if (response.ok) {
      console.log('✅ Form submitted successfully to HubSpot');
      return true;
    } else {
      const errorText = await response.text();
      console.log('⚠️ HubSpot form submission failed:', response.status, errorText);
      return false;
    }
  } catch (error) {
    console.error('❌ HubSpot form submission error:', error);
    return false;
  }
};

// Call this when the app loads
if (typeof window !== 'undefined') {
  console.log('Setting up HubSpot tracking on page load');

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeHubSpotTracking);
  } else {
    initializeHubSpotTracking();
  }
}